// ./src/components/features/SandboxCodePreview/SandboxCodePreview.tsx
import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  lazy,
  Suspense,
} from "react";
import {
  Box,
  Paper,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  CopyButton,
  SegmentedControl,
  Loader,
  Modal,
} from "@mantine/core";

import { AppIcon } from "@/components/generic";
import { SandboxFormPreview } from "@/components/features/SandboxFormPreview";

// Lazy load CodeMirror
const CodeMirror = lazy(() => import("@uiw/react-codemirror"));

// Lazy load HTML language support
const loadHtmlExtension = () =>
  import("@codemirror/lang-html").then((mod) => mod.html());

const SCRIPTS_ROOT_URL =
  import.meta.env.VITE_SCRIPTS_ROOT_URL ||
  "https://api.formsubmits.com/api/scripts";

const DEBOUNCE_DELAY = 1000; // 1 second

export interface SandboxCodePreviewProps {
  /** The code to display */
  code: string;
  /** Loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
  /** Callback when code changes (only works when editable is true) */
  onChange?: (code: string) => void;
  /** Whether the code can be edited */
  editable?: boolean;
  /** Filename for download (without extension). Defaults to "form" */
  downloadFilename?: string;
  /** Form name for preview title */
  formName?: string;
  /** Current theme for preview */
  theme?: string;
}

/**
 * Sanitize string for use as filename
 */
const sanitizeFilename = (name: string): string => {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .substring(0, 50) || "form"
  );
};

/**
 * Custom hook for debouncing a value
 */
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Inner component that uses CodeMirror (loaded lazily)
const CodeMirrorEditor: React.FC<{
  code: string;
  isReadOnly: boolean;
  onChange: (value: string) => void;
  height?: string;
}> = ({ code, isReadOnly, onChange, height = "100%" }) => {
  const [extensions, setExtensions] = useState<any[]>([]);
  const [extensionsLoaded, setExtensionsLoaded] = useState(false);

  // Load extensions on mount
  useEffect(() => {
    let mounted = true;

    const loadExtensions = async () => {
      try {
        const [htmlExt, viewMod] = await Promise.all([
          loadHtmlExtension(),
          import("@codemirror/view").then((mod) => mod.EditorView),
        ]);

        if (mounted) {
          setExtensions([htmlExt, viewMod.lineWrapping]);
          setExtensionsLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load CodeMirror extensions:", error);
        if (mounted) {
          setExtensionsLoaded(true); // Still show editor without extensions
        }
      }
    };

    loadExtensions();

    return () => {
      mounted = false;
    };
  }, []);

  if (!extensionsLoaded) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Loader size="sm" />
      </Box>
    );
  }

  return (
    <CodeMirror
      value={code}
      height={height}
      extensions={extensions}
      onChange={onChange}
      readOnly={isReadOnly}
      editable={!isReadOnly}
      basicSetup={{
        lineNumbers: true,
        highlightActiveLineGutter: !isReadOnly,
        highlightActiveLine: !isReadOnly,
        foldGutter: true,
        dropCursor: !isReadOnly,
        allowMultipleSelections: !isReadOnly,
        indentOnInput: !isReadOnly,
        bracketMatching: true,
        closeBrackets: !isReadOnly,
        autocompletion: !isReadOnly,
        rectangularSelection: !isReadOnly,
        crosshairCursor: false,
        highlightSelectionMatches: true,
        closeBracketsKeymap: !isReadOnly,
        searchKeymap: true,
        foldKeymap: true,
        completionKeymap: !isReadOnly,
        lintKeymap: false,
      }}
      style={{
        height: "100%",
        fontSize: "0.8125rem",
      }}
    />
  );
};

export const SandboxCodePreview: React.FC<SandboxCodePreviewProps> = ({
  code,
  loading = false,
  error = null,
  onChange,
  editable = false,
  downloadFilename = "form",
  formName,
  theme = "default",
}) => {
  const [viewMode, setViewMode] = useState<string>("html");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenCode, setFullscreenCode] = useState<string>("");

  // Debounce the fullscreen code for preview updates
  const debouncedFullscreenCode = useDebounce(fullscreenCode, DEBOUNCE_DELAY);

  // Sync fullscreen code when opening fullscreen or when external code changes
  useEffect(() => {
    if (isFullscreen) {
      setFullscreenCode(code);
    }
  }, [isFullscreen, code]);

  // Generate embed script code
  const getEmbedScriptCode = useCallback((sourceCode: string) => {
    // Extract form path from the action URL in the HTML
    const actionMatch = sourceCode.match(/action="[^"]*\/submit\/([^"]+)"/);
    const formPath = actionMatch ? actionMatch[1] : "YOUR_FORM_PATH";

    return `<!-- FormSubmits Embed -->
<div id="formsubmits-form"></div>
<script
  src="${SCRIPTS_ROOT_URL}/formsubmits-embed.js"
  data-form-path="${formPath}"
  data-container="formsubmits-form"
  data-theme="default"
></script>

<!-- Optional: Include FormSubmits handler for AJAX submissions -->
<script src="${SCRIPTS_ROOT_URL}/formsubmits-core.js"></script>`;
  }, []);

  const displayCode = viewMode === "html" ? code : getEmbedScriptCode(code);

  // Handle code change from editor (inline)
  const handleCodeChange = useCallback(
    (value: string) => {
      if (editable && onChange) {
        onChange(value);
      }
    },
    [editable, onChange]
  );

  // Handle code change in fullscreen mode
  const handleFullscreenCodeChange = useCallback((value: string) => {
    setFullscreenCode(value);
  }, []);

  // Handle fullscreen close - apply changes
  const handleFullscreenClose = useCallback(() => {
    // Apply the final code changes when closing
    if (editable && onChange && fullscreenCode !== code) {
      onChange(fullscreenCode);
    }
    setIsFullscreen(false);
  }, [editable, onChange, fullscreenCode, code]);

  // Handle download
  const handleDownload = useCallback(() => {
    const codeToDownload = isFullscreen ? fullscreenCode : displayCode;
    if (!codeToDownload) return;

    const filename = `${sanitizeFilename(downloadFilename)}.html`;
    const blob = new Blob([codeToDownload], { type: "text/html" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }, [displayCode, fullscreenCode, isFullscreen, downloadFilename]);

  // Determine if editor should be read-only
  // Embed script view is always read-only since it's generated
  const isReadOnly = !editable || viewMode === "embed";

  // Loading fallback for Suspense
  const editorFallback = useMemo(
    () => (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          backgroundColor: "var(--mantine-color-gray-0)",
        }}
      >
        <Loader size="sm" />
      </Box>
    ),
    []
  );

  // Header actions component (reused in both views)
  const renderHeaderActions = (
    codeValue: string,
    showFullscreenToggle: boolean
  ) => (
    <Group gap="xs">
      <CopyButton value={codeValue}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied!" : "Copy code"}>
            <ActionIcon
              variant={copied ? "filled" : "subtle"}
              color={copied ? "green" : "gray"}
              onClick={copy}
              disabled={loading || !codeValue}
            >
              <AppIcon iconName={copied ? "Check" : "Copy"} size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
      <Tooltip label="Download HTML">
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={handleDownload}
          disabled={loading || !codeValue}
        >
          <AppIcon iconName="Download" size={16} />
        </ActionIcon>
      </Tooltip>
      {showFullscreenToggle && editable && (
        <Tooltip label={isFullscreen ? "Exit fullscreen" : "Edit fullscreen"}>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => setIsFullscreen(!isFullscreen)}
            disabled={loading}
          >
            <AppIcon
              iconName={isFullscreen ? "Minimize" : "Maximize"}
              size={16}
            />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  return (
    <>
      <Paper
        radius="md"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Group
          justify="space-between"
          px="md"
          py="xs"
          style={{
            borderBottom: "1px solid var(--mantine-color-gray-3)",
            backgroundColor: "var(--mantine-color-gray-0)",
            flexShrink: 0,
          }}
        >
          <Group gap="md">
            <Text size="sm" fw={500} c="dimmed">
              Code
            </Text>
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={setViewMode}
              data={[
                { label: "Full HTML", value: "html" },
                { label: "Embed Script", value: "embed" },
              ]}
            />
          </Group>

          {renderHeaderActions(displayCode, true)}
        </Group>

        {/* Content */}
        <Box style={{ flex: 1, overflow: "hidden" }}>
          {loading ? (
            <Box
              p="xl"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Text c="dimmed" size="sm">
                Generating code...
              </Text>
            </Box>
          ) : error ? (
            <Box
              p="xl"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Text c="red" size="sm">
                {error}
              </Text>
            </Box>
          ) : !displayCode ? (
            <Box
              p="xl"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                backgroundColor: "var(--mantine-color-gray-0)",
              }}
            >
              <Text c="dimmed" size="sm">
                Select a form to generate code
              </Text>
            </Box>
          ) : (
            <Suspense fallback={editorFallback}>
              <CodeMirrorEditor
                code={displayCode}
                isReadOnly={isReadOnly}
                onChange={handleCodeChange}
              />
            </Suspense>
          )}
        </Box>
      </Paper>

      {/* Fullscreen Modal */}
      <Modal
        opened={isFullscreen}
        onClose={handleFullscreenClose}
        fullScreen
        withCloseButton={false}
        padding={0}
        styles={{
          body: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            height: "100vh",
          },
        }}
      >
        <Box
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Fullscreen Header */}
          <Group
            justify="space-between"
            px="md"
            py="sm"
            style={{
              borderBottom: "1px solid var(--mantine-color-gray-3)",
              backgroundColor: "var(--mantine-color-gray-0)",
              flexShrink: 0,
            }}
          >
            <Group gap="md">
              <Text size="sm" fw={500}>
                {formName ? `Editing: ${formName}` : "Code Editor"}
              </Text>
            </Group>

            <Group gap="xs">
              {renderHeaderActions(fullscreenCode, false)}
              <Tooltip label="Close">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={handleFullscreenClose}
                >
                  <AppIcon iconName="X" size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {/* Fullscreen Content - Split View */}
          <Box
            style={{
              flex: 1,
              display: "flex",
              overflow: "hidden",
            }}
          >
            {/* Code Editor Panel */}
            <Box
              style={{
                flex: 1,
                borderRight: "1px solid var(--mantine-color-gray-3)",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Group
                px="md"
                py="xs"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                  backgroundColor: "var(--mantine-color-gray-1)",
                  flexShrink: 0,
                }}
              >
                <Text size="xs" fw={500} c="dimmed">
                  HTML Code
                </Text>
              </Group>
              <Box style={{ flex: 1, overflow: "hidden" }}>
                <Suspense fallback={editorFallback}>
                  <CodeMirrorEditor
                    code={fullscreenCode}
                    isReadOnly={false}
                    onChange={handleFullscreenCodeChange}
                  />
                </Suspense>
              </Box>
            </Box>

            {/* Preview Panel */}
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Group
                px="md"
                py="xs"
                style={{
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                  backgroundColor: "var(--mantine-color-gray-1)",
                  flexShrink: 0,
                }}
              >
                <Text size="xs" fw={500} c="dimmed">
                  Preview
                </Text>
                {fullscreenCode !== debouncedFullscreenCode && (
                  <Text size="xs" c="orange">
                    (updating...)
                  </Text>
                )}
              </Group>
              <Box style={{ flex: 1, overflow: "hidden" }}>
                <SandboxFormPreview
                  code={debouncedFullscreenCode}
                  formName={formName}
                  loading={false}
                  error={null}
                  theme={theme}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
